import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { api, upload, type Me } from "./api";
import { useProblems, FieldProblem, invalid, type Rule } from "./form-check";
import { eastern } from "./RecordScreen";

/**
 * Medication return (build plan §11.1 #1). Everything the med station holds
 * goes back: by city from the counselors' signed sheet (non-controlled), or
 * one student at the station (a pickup, a dismissal, and every controlled
 * medication: counted with a second person and signed by the parent).
 * Phone-first: this screen is used at the med station's counter.
 */

interface Doc { docKey: string; fileName: string; notes: string; uploadedAtUtc: string; uploadedByEmail: string }
interface Returned { status: "Returned" | "NotReturned" | "Disposed"; qty: number | null; toRole: string; toName: string; relationship: string; reason: string; verifierEmail: string; receiptDocKey: string; sheetDocKey: string; discrepancy: number | null; note: string; atUtc: string; byName: string; byEmail: string }
interface Line {
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  departed: boolean;
  medKey: string;
  medication: string;
  controlled: boolean;
  heldNotAuthorized: boolean;
  receivedQty: number | null;
  unitsGiven: number | null;
  expected: number | null;
  expectedLabel: string;
  doses: unknown[];
  returned: Returned | null;
}
interface View { lines: Line[]; sheets: Doc[]; cities: string[] }

const NOT_ASSIGNED = "(not assigned)";
const cityLabel = (c: string) => (c === NOT_ASSIGNED || !c ? "Not assigned" : c);
const STATUS_LABEL: Record<string, string> = { Returned: "Returned", NotReturned: "Not returned", Disposed: "Disposed" };
const TO_LABEL: Record<string, string> = { Parent: "Parent or guardian", Student: "Student", Counselor: "Counselor" };

function SecondPerson({ idp, email, pin, setEmail, setPin, problems }: { idp: string; email: string; pin: string; setEmail: (v: string) => void; setPin: (v: string) => void; problems: Record<string, string> }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <div><label htmlFor={`${idp}-ve`} className="field-label">Second person's email</label><input id={`${idp}-ve`} type="email" autoComplete="off" className="field-input" {...invalid(`${idp}-ve`, problems)} value={email} onChange={(e) => setEmail(e.target.value)} /><FieldProblem id={`${idp}-ve`} problems={problems} /></div>
      <div><label htmlFor={`${idp}-vp`} className="field-label">Their PIN</label><input id={`${idp}-vp`} type="password" inputMode="numeric" autoComplete="off" maxLength={4} className="field-input" {...invalid(`${idp}-vp`, problems)} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} /><FieldProblem id={`${idp}-vp`} problems={problems} /></div>
    </div>
  );
}

function ReturnedSummary({ r }: { r: Returned }) {
  return (
    <p className="text-sm">
      <span className="rounded bg-emerald-100 px-1.5 text-xs font-semibold text-emerald-900">{STATUS_LABEL[r.status]}</span>{" "}
      {r.qty !== null && <>{r.qty} · </>}
      {r.toName || TO_LABEL[r.toRole] || ""}{r.relationship ? ` (${r.relationship})` : ""}
      {r.reason ? ` · ${r.reason}` : ""}
      {r.discrepancy ? <b className="text-red-700"> · discrepancy {r.discrepancy}: {r.note}</b> : null}
      <span className="block text-xs text-slate-500">{eastern(r.atUtc)} · {r.byName || r.byEmail}{r.verifierEmail ? ` · with ${r.verifierEmail}` : ""}{r.receiptDocKey && <> · <a className="text-program underline" href={`/api/med-documents/${encodeURIComponent(r.receiptDocKey)}`} target="_blank" rel="noreferrer">signed receipt</a></>}{r.sheetDocKey && <> · <a className="text-program underline" href={`/api/med-documents/${encodeURIComponent(r.sheetDocKey)}`} target="_blank" rel="noreferrer">signed sheet</a></>}</span>
    </p>
  );
}

/** A non-controlled medication: Returned (how many, to whom), Not returned (why), or Disposed (why, second person). */
function EntryForm({ l, sheetDocKey, path, onSaved }: { l: Line; sheetDocKey: string; path: "CounselorSheet" | "MedStationToParent"; onSaved: (l: Line) => void }) {
  const idp = `rt-${l.medKey}`;
  const [status, setStatus] = useState<"Returned" | "NotReturned" | "Disposed">("Returned");
  const [qty, setQty] = useState("");
  const [toRole, setToRole] = useState(path === "MedStationToParent" ? "Parent" : "");
  const [toName, setToName] = useState("");
  const [reason, setReason] = useState("");
  const [vEmail, setVEmail] = useState("");
  const [vPin, setVPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rules: Rule[] = [
    [`${idp}-q`, status === "Returned" && !/^\d{1,4}$/.test(qty), "Enter how many were returned, as a whole number."],
    [`${idp}-to`, status === "Returned" && !toRole, "Choose who it was returned to: parent, student or counselor."],
    [`${idp}-tn`, status === "Returned" && toRole === "Parent" && !toName.trim(), "Write the name of who received it."],
    [`${idp}-r`, status !== "Returned" && !reason.trim(), status === "Disposed" ? "Write why it was disposed of." : "Write why it was not returned."],
    [`${idp}-ve`, status === "Disposed" && !vEmail.trim(), "The second person enters their own email and 4-digit PIN."],
    [`${idp}-vp`, status === "Disposed" && !/^\d{4}$/.test(vPin), "The second person enters their own email and 4-digit PIN."],
  ];
  const { problems, check } = useProblems(rules);
  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ line: Line }>("POST", `/api/returns/${encodeURIComponent(l.medKey)}`, { status, qty, toRole, toName, reason, path, sheetDocKey, ...(status === "Disposed" ? { verifier: { email: vEmail, pin: vPin } } : {}) });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not saved.");
    onSaved(r.data.line);
  }
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What happened">
        {(["Returned", "NotReturned", "Disposed"] as const).map((x) => (
          <button key={x} type="button" role="radio" aria-checked={status === x} className={`min-h-11 rounded-md border px-3 text-sm ${status === x ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => setStatus(x)}>{STATUS_LABEL[x]}</button>
        ))}
      </div>
      {status === "Returned" && (
        <div className="grid gap-2 sm:grid-cols-3">
          <div><label htmlFor={`${idp}-q`} className="field-label">How many</label><input id={`${idp}-q`} inputMode="numeric" className="field-input" {...invalid(`${idp}-q`, problems)} value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} /><FieldProblem id={`${idp}-q`} problems={problems} /></div>
          <div><label htmlFor={`${idp}-to`} className="field-label">To</label><select id={`${idp}-to`} className="field-input" {...invalid(`${idp}-to`, problems)} value={toRole} onChange={(e) => setToRole(e.target.value)}><option value="">Choose…</option>{Object.entries(TO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select><FieldProblem id={`${idp}-to`} problems={problems} /></div>
          <div><label htmlFor={`${idp}-tn`} className="field-label">Name{toRole === "Parent" ? "" : " (optional)"}</label><input id={`${idp}-tn`} className="field-input" {...invalid(`${idp}-tn`, problems)} value={toName} onChange={(e) => setToName(e.target.value)} /><FieldProblem id={`${idp}-tn`} problems={problems} /></div>
        </div>
      )}
      {status !== "Returned" && (
        <div><label htmlFor={`${idp}-r`} className="field-label">{status === "Disposed" ? "Why it was disposed of" : "Why it was not returned"}</label><input id={`${idp}-r`} className="field-input" {...invalid(`${idp}-r`, problems)} value={reason} onChange={(e) => setReason(e.target.value)} /><FieldProblem id={`${idp}-r`} problems={problems} /></div>
      )}
      {status === "Disposed" && (
        <>
          <div className="max-w-40"><label htmlFor={`${idp}-dq`} className="field-label">How many (optional)</label><input id={`${idp}-dq`} inputMode="numeric" className="field-input" value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} /></div>
          <SecondPerson idp={idp} email={vEmail} pin={vPin} setEmail={setVEmail} setPin={setVPin} problems={problems} />
        </>
      )}
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void save()}>Record the return</button>
    </div>
  );
}

/** A controlled medication, at the med station: the count, a second person, the parent's signature; the chain check. */
function ControlledForm({ l, onSaved }: { l: Line; onSaved: (l: Line) => void }) {
  const idp = `rc-${l.medKey}`;
  const [status, setStatus] = useState<"Returned" | "Disposed">("Returned");
  const [count, setCount] = useState("");
  const [toName, setToName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [vEmail, setVEmail] = useState("");
  const [vPin, setVPin] = useState("");
  const [drawn, setDrawn] = useState("");
  const pad = useRef<SignatureCanvas | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = /^\d{1,4}$/.test(count) ? Number(count) : null;
  const discrepancy = n !== null && l.expected !== null ? l.expected - n : null;
  const needsNote = n !== null && discrepancy !== 0;
  const rules: Rule[] = [
    [`${idp}-c`, n === null, "Count what is being returned, as a whole number (0 if none)."],
    [`${idp}-tn`, status === "Returned" && !toName.trim(), "Write the name of the parent or guardian receiving it."],
    [`${idp}-rel`, status === "Returned" && !relationship.trim(), "Write their relationship to the student."],
    [`${idp}-sig`, status === "Returned" && !drawn, "The parent or guardian signs in the box."],
    [`${idp}-r`, status === "Disposed" && !reason.trim(), "Write why it was disposed of."],
    [`${idp}-n`, needsNote && !note.trim(), "The count doesn't reconcile: write a note."],
    [`${idp}-ve`, !vEmail.trim(), "The second person enters their own email and 4-digit PIN."],
    [`${idp}-vp`, !/^\d{4}$/.test(vPin), "The second person enters their own email and 4-digit PIN."],
  ];
  const { problems, check } = useProblems(rules);
  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ line: Line }>("POST", `/api/returns/${encodeURIComponent(l.medKey)}/controlled`, { status, count, toName, relationship, reason, note, signature: drawn, verifier: { email: vEmail, pin: vPin } });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not saved.");
    onSaved(r.data.line);
  }
  return (
    <div className="mt-2 space-y-2 rounded-md border border-violet-200 bg-violet-50/40 p-2">
      <p className="text-sm">Received <b>{l.receivedQty ?? "?"}</b> · given <b>{l.unitsGiven ?? "?"}</b> in {l.doses.length} dose{l.doses.length === 1 ? "" : "s"} · expected back <b>{l.expectedLabel}</b></p>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What happened">
        {(["Returned", "Disposed"] as const).map((x) => (
          <button key={x} type="button" role="radio" aria-checked={status === x} className={`min-h-11 rounded-md border px-3 text-sm ${status === x ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => setStatus(x)}>{x === "Returned" ? "Returned to the parent" : "Disposed"}</button>
        ))}
      </div>
      <div className="max-w-40"><label htmlFor={`${idp}-c`} className="field-label">Count, with the second person</label><input id={`${idp}-c`} inputMode="numeric" className="field-input" {...invalid(`${idp}-c`, problems)} value={count} onChange={(e) => setCount(e.target.value.replace(/\D/g, ""))} /><FieldProblem id={`${idp}-c`} problems={problems} /></div>
      {n !== null && l.expected !== null && <p className={`text-sm font-semibold ${discrepancy === 0 ? "text-emerald-800" : "text-red-700"}`}>{discrepancy === 0 ? "It reconciles: received − given − returned = 0." : `It doesn't reconcile: ${discrepancy} unaccounted for.`}</p>}
      {(needsNote || l.expected === null) && (
        <div><label htmlFor={`${idp}-n`} className="field-label">Note{needsNote ? "" : " (optional)"}</label><input id={`${idp}-n`} className="field-input" {...invalid(`${idp}-n`, problems)} value={note} onChange={(e) => setNote(e.target.value)} /><FieldProblem id={`${idp}-n`} problems={problems} /></div>
      )}
      {status === "Returned" ? (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <div><label htmlFor={`${idp}-tn`} className="field-label">Parent or guardian receiving it</label><input id={`${idp}-tn`} className="field-input" {...invalid(`${idp}-tn`, problems)} value={toName} onChange={(e) => setToName(e.target.value)} /><FieldProblem id={`${idp}-tn`} problems={problems} /></div>
            <div><label htmlFor={`${idp}-rel`} className="field-label">Relationship</label><input id={`${idp}-rel`} className="field-input" {...invalid(`${idp}-rel`, problems)} value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. Mother" /><FieldProblem id={`${idp}-rel`} problems={problems} /></div>
          </div>
          <p id={`${idp}-sig`} className="field-label">Their signature</p>
          <div className="max-w-md rounded border border-slate-300 bg-white">
            <SignatureCanvas ref={(x) => { pad.current = x; }} penColor="#1e293b" canvasProps={{ width: 420, height: 120, className: "h-[120px] w-full rounded" }} onEnd={() => setDrawn(pad.current && !pad.current.isEmpty() ? pad.current.toDataURL("image/png") : "")} />
            <div className="flex justify-end p-1"><button type="button" className="text-xs text-slate-500 underline" onClick={() => { pad.current?.clear(); setDrawn(""); }}>Clear</button></div>
          </div>
          <FieldProblem id={`${idp}-sig`} problems={problems} />
        </>
      ) : (
        <div><label htmlFor={`${idp}-r`} className="field-label">Why it was disposed of</label><input id={`${idp}-r`} className="field-input" {...invalid(`${idp}-r`, problems)} value={reason} onChange={(e) => setReason(e.target.value)} /><FieldProblem id={`${idp}-r`} problems={problems} /></div>
      )}
      <SecondPerson idp={idp} email={vEmail} pin={vPin} setEmail={setVEmail} setPin={setVPin} problems={problems} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void save()}>{status === "Returned" ? "Record the return and save the receipt" : "Record the disposal"}</button>
    </div>
  );
}

function LineCard({ l, sheetDocKey, path, onSaved }: { l: Line; sheetDocKey: string; path: "CounselorSheet" | "MedStationToParent"; onSaved: (l: Line) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="py-3">
      <p className="text-sm">
        <span className="font-medium">{l.lastName}, {l.firstName}</span>
        {l.departed && <span className="ml-2 rounded bg-amber-200 px-1.5 text-xs font-semibold text-amber-950">left the session</span>}
        <span className="text-slate-600"> · {cityLabel(l.city)}</span>
      </p>
      <p className="text-sm">{l.medication}{l.controlled && <span className="ml-1 rounded bg-violet-100 px-1 text-xs font-semibold text-violet-900">controlled</span>}{l.heldNotAuthorized && <span className="ml-1 text-xs text-slate-500">(held: not authorized)</span>} · expected: {l.expectedLabel}</p>
      {l.returned ? <ReturnedSummary r={l.returned} /> : !open ? (
        <button type="button" className="btn-secondary mt-1 min-h-11" onClick={() => setOpen(true)}>{l.controlled ? "Return at the med station" : "Enter the return"}</button>
      ) : l.controlled ? <ControlledForm l={l} onSaved={onSaved} /> : <EntryForm l={l} sheetDocKey={sheetDocKey} path={path} onSaved={onSaved} />}
    </li>
  );
}

function Lines({ lines, sheetDocKey, path, onSaved }: { lines: Line[]; sheetDocKey: string; path: "CounselorSheet" | "MedStationToParent"; onSaved: (l: Line) => void }) {
  if (!lines.length) return <p className="text-sm text-slate-600">Nothing held by the med station.</p>;
  const left = lines.filter((l) => !l.returned).length;
  return (
    <>
      <p className="text-sm text-slate-600" role="status">{left} still to return · {lines.length - left} recorded</p>
      <ul className="divide-y divide-slate-100">{lines.map((l) => <LineCard key={l.medKey} l={l} sheetDocKey={sheetDocKey} path={path} onSaved={onSaved} />)}</ul>
    </>
  );
}

function ByCity({ me, cities }: { me: Me; cities: string[] }) {
  const [city, setCity] = useState("");
  const [v, setV] = useState<View | null>(null);
  const [sheet, setSheet] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = (c = city) => {
    if (!c) return setV(null);
    void api<View>("GET", `/api/returns?city=${encodeURIComponent(c)}`).then((r) => {
      if (!r.ok) return setError(r.data.error ?? "Could not load.");
      setV(r.data);
      setSheet((s) => s || r.data.sheets[0]?.docKey || "");
    });
  };
  useEffect(() => {
    setSheet("");
    load(city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);
  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setBusy(true);
    const r = await upload<{ sheet: Doc; sheets: Doc[] }>(`/api/returns/sheet?city=${encodeURIComponent(city)}&name=${encodeURIComponent(file.name)}`, file);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not uploaded.");
    setV((x) => (x ? { ...x, sheets: r.data.sheets } : x));
    setSheet(r.data.sheet.docKey);
  }
  return (
    <div className="space-y-3">
      <div className="section-card space-y-2">
        <label htmlFor="rt-city" className="field-label">City</label>
        <select id="rt-city" className="field-input" value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">Choose a city…</option>
          {cities.map((c) => <option key={c} value={c}>{cityLabel(c)}</option>)}
        </select>
        {city && me.capabilities.includes("export.phi") && <a className="btn-secondary inline-flex min-h-11 items-center" href={`/api/print/return-sheet?city=${encodeURIComponent(city)}`}>Print this city's return sheet</a>}
      </div>
      {city && v && (
        <>
          <div className="section-card space-y-2">
            <h3 className="text-base font-semibold">The signed sheet</h3>
            <p className="text-sm text-slate-600">Take a photo of the counselors' signed sheet once; each return you enter below is linked to it.</p>
            {v.sheets.length > 0 && (
              <div>
                <label htmlFor="rt-sheet" className="field-label">Link the returns to</label>
                <select id="rt-sheet" className="field-input" value={sheet} onChange={(e) => setSheet(e.target.value)}>
                  {v.sheets.map((d) => <option key={d.docKey} value={d.docKey}>{d.fileName.replace(/^[0-9A-Z]{26}-ReturnSheet-/, "")} · {eastern(d.uploadedAtUtc)}</option>)}
                  <option value="">No sheet</option>
                </select>
              </div>
            )}
            <label htmlFor="rt-file" className="btn-secondary inline-flex min-h-11 cursor-pointer items-center">{busy ? "Uploading…" : "Take or choose a photo of the sheet"}</label>
            <input id="rt-file" type="file" accept="image/*,application/pdf" capture="environment" className="sr-only" onChange={(e) => void onFile(e.target.files?.[0])} />
            {error && <p className="field-error" role="alert">{error}</p>}
          </div>
          <div className="section-card">
            <Lines lines={v.lines} sheetDocKey={sheet} path="CounselorSheet" onSaved={(l) => setV((x) => (x ? { ...x, lines: x.lines.map((y) => (y.medKey === l.medKey ? l : y)) } : x))} />
          </div>
        </>
      )}
    </div>
  );
}

function OneStudent() {
  const [q, setQ] = useState("");
  const [people, setPeople] = useState<{ personKey: string; lastName: string; firstName: string; city: string }[]>([]);
  const [who, setWho] = useState<{ personKey: string; lastName: string; firstName: string } | null>(null);
  const [lines, setLines] = useState<Line[] | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (!term) return setPeople([]);
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      const r = await api<{ people: typeof people }>("GET", `/api/station/search?q=${encodeURIComponent(term)}`);
      if (mine === seq.current && r.ok) setPeople(r.data.people.filter((p) => !p.personKey.startsWith("STAFF-")));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!who) return setLines(null);
    void api<View>("GET", `/api/returns?personKey=${encodeURIComponent(who.personKey)}`).then((r) => r.ok && setLines(r.data.lines));
  }, [who]);
  return (
    <div className="space-y-3">
      <div className="section-card">
        <label htmlFor="rt-q" className="field-label">Find the student (a pickup, a dismissal, a controlled medication)</label>
        <input id="rt-q" className="field-input" value={q} onChange={(e) => { setQ(e.target.value); setWho(null); }} autoComplete="off" />
        {!who && people.length > 0 && (
          <ul className="mt-2 divide-y divide-slate-100 text-sm">
            {people.map((p) => <li key={p.personKey} className="py-1"><button type="button" className="min-h-11 text-left font-medium text-program underline" onClick={() => setWho(p)}>{p.lastName}, {p.firstName}</button> <span className="text-slate-500">{p.personKey} · {cityLabel(p.city)}</span></li>)}
          </ul>
        )}
      </div>
      {who && lines && (
        <div className="section-card">
          <h3 className="mb-1 text-base font-semibold">{who.lastName}, {who.firstName}</h3>
          <Lines lines={lines} sheetDocKey="" path="MedStationToParent" onSaved={(l) => setLines((x) => (x ? x.map((y) => (y.medKey === l.medKey ? l : y)) : x))} />
        </div>
      )}
    </div>
  );
}

export function Returns({ me }: { me: Me }): React.ReactElement {
  const [mode, setMode] = useState<"city" | "student">("city");
  const [cities, setCities] = useState<string[]>([]);
  useEffect(() => {
    void api<{ cities: string[] }>("GET", "/api/returns/options").then((r) => r.ok && setCities(r.data.cities));
  }, []);
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Medication return</h2>
      <p className="mb-3 text-sm text-slate-600">Everything the med station holds goes back. By city from the counselors' signed sheet, or one student at the med station: a pickup, a dismissal, and every controlled medication (counted with a second person and signed by the parent).</p>
      <div className="mb-3 flex gap-2" role="tablist">
        {([["city", "By city (the sheet)"], ["student", "One student"]] as const).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={mode === k} className={`min-h-11 rounded-md border px-3 text-sm ${mode === k ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => setMode(k)}>{label}</button>
        ))}
      </div>
      {mode === "city" ? <ByCity me={me} cities={cities} /> : <OneStudent />}
    </section>
  );
}
